import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Clock, Users, Star, ShoppingCart, CheckCircle, Play } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

const CourseDetailPage = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);
  const [hasPurchased, setHasPurchased] = useState(false);

  useEffect(() => {
    fetchCourse();
  }, [courseId]);

  const fetchCourse = async () => {
    try {
      const response = await axios.get(`/courses/${courseId}`);
      setCourse(response.data.course);
      
      // Vérifier si l'utilisateur a déjà acheté ce cours
      if (isAuthenticated) {
        checkPurchaseStatus();
      }
    } catch (error) {
      console.error('Erreur lors du chargement du cours:', error);
      toast.error('Cours non trouvé');
      navigate('/courses');
    } finally {
      setLoading(false);
    }
  };

  const checkPurchaseStatus = async () => {
    try {
      const response = await axios.get('/users/dashboard');
      const purchasedCourses = response.data.courses || [];
      const purchased = purchasedCourses.some(p => p.course.id === courseId);
      setHasPurchased(purchased);
    } catch (error) {
      console.error('Erreur lors de la vérification de l\'achat:', error);
    }
  };

  const handlePurchase = async () => {
    if (!isAuthenticated) {
      toast.error('Veuillez vous connecter pour acheter ce cours');
      navigate('/login');
      return;
    }

    setPurchasing(true);
    try {
      const response = await axios.post('/payments/create-checkout-session', {
        courseId: courseId
      });

      // Rediriger vers Stripe Checkout
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Erreur lors de la création de la session de paiement:', error);
      const errorMessage = error.response?.data?.error || 'Erreur lors de la création du paiement';
      toast.error(errorMessage);
    } finally {
      setPurchasing(false);
    }
  };

  const formatDuration = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h${mins > 0 ? ` ${mins}min` : ''}`;
    }
    return `${mins}min`;
  };

  const formatPrice = (price, currency) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: currency
    }).format(price);
  };

  const getLevelColor = (level) => {
    switch (level?.toLowerCase()) {
      case 'débutant':
        return 'bg-green-100 text-green-800';
      case 'intermédiaire':
        return 'bg-yellow-100 text-yellow-800';
      case 'avancé':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Cours non trouvé</h1>
            <button onClick={() => navigate('/courses')} className="btn btn-primary">
              Retour aux cours
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Contenu principal */}
          <div className="lg:col-span-2">
            {/* En-tête du cours */}
            <div className="bg-white rounded-lg shadow-lg overflow-hidden mb-8">
              <div className="h-64 bg-gradient-to-r from-primary-400 to-primary-600 relative">
                {course.thumbnail ? (
                  <img 
                    src={course.thumbnail} 
                    alt={course.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <span className="text-white text-2xl font-medium">{course.title}</span>
                  </div>
                )}
                <div className="absolute top-4 left-4">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${getLevelColor(course.level)}`}>
                    {course.level}
                  </span>
                </div>
              </div>
              
              <div className="p-6">
                <h1 className="text-3xl font-bold text-gray-900 mb-4">{course.title}</h1>
                <p className="text-gray-600 text-lg leading-relaxed">{course.description}</p>
                
                {/* Statistiques du cours */}
                <div className="flex items-center space-x-6 mt-6 text-sm text-gray-500">
                  <div className="flex items-center">
                    <Clock className="w-5 h-5 mr-2" />
                    {formatDuration(course.duration)}
                  </div>
                  <div className="flex items-center">
                    <Users className="w-5 h-5 mr-2" />
                    {course._count?.purchases || 0} étudiants
                  </div>
                  <div className="flex items-center">
                    <Play className="w-5 h-5 mr-2" />
                    {course._count?.videos || 0} vidéos
                  </div>
                </div>
              </div>
            </div>

            {/* Contenu du cours */}
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Contenu du cours</h2>
              {course.videos && course.videos.length > 0 ? (
                <div className="space-y-3">
                  {course.videos.map((video, index) => (
                    <div key={video.id} className="flex items-center p-3 border border-gray-200 rounded-lg">
                      <div className="flex-shrink-0 w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center mr-3">
                        <span className="text-primary-600 font-medium text-sm">{index + 1}</span>
                      </div>
                      <div className="flex-1">
                        <h3 className="font-medium text-gray-900">{video.title}</h3>
                        <p className="text-sm text-gray-500">{formatDuration(Math.floor(video.duration / 60))}</p>
                      </div>
                      {hasPurchased ? (
                        <CheckCircle className="w-5 h-5 text-green-500" />
                      ) : (
                        <div className="w-5 h-5 bg-gray-300 rounded-full"></div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500">Le contenu du cours sera bientôt disponible.</p>
              )}
            </div>
          </div>

          {/* Sidebar d'achat */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-lg p-6 sticky top-8">
              <div className="text-center mb-6">
                <div className="text-4xl font-bold text-primary-600 mb-2">
                  {formatPrice(course.price, course.currency)}
                </div>
                <p className="text-gray-500">Accès à vie</p>
              </div>

              {hasPurchased ? (
                <div className="text-center">
                  <div className="flex items-center justify-center text-green-600 mb-4">
                    <CheckCircle className="w-6 h-6 mr-2" />
                    <span className="font-medium">Cours acheté</span>
                  </div>
                  <button 
                    onClick={() => navigate(`/watch/${courseId}`)}
                    className="w-full btn btn-primary"
                  >
                    Accéder au cours
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  <button
                    onClick={handlePurchase}
                    disabled={purchasing}
                    className="w-full btn btn-primary flex items-center justify-center"
                  >
                    {purchasing ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Redirection...
                      </>
                    ) : (
                      <>
                        <ShoppingCart className="w-5 h-5 mr-2" />
                        Acheter maintenant
                      </>
                    )}
                  </button>
                </div>
              )}

              <div className="text-center text-sm text-gray-500">
                <p>💳 Paiement sécurisé par Stripe</p>
                <p>🔒 Garantie satisfait ou remboursé 30 jours</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CourseDetailPage;
